(() => {
  /* Header search — the theme's DialogElement already opens the panel for any
     [aria-controls="<dialog id>"] trigger, but it leaves focus on the trigger,
     stranding keyboard users behind the overlay. Move focus on open instead of
     wiring our own click handler, so every trigger benefits and we don't race
     the theme's toggle. */
  const headerSearch = document.querySelector('header-search');
  if (headerSearch) {
    new MutationObserver(() => {
      if (headerSearch.hasAttribute('open')) {
        headerSearch.querySelector('input[name="q"]')?.focus();
      }
    }).observe(headerSearch, { attributes: true, attributeFilter: ['open'] });
  }

  /* Header dropdowns — choose the side with the most room, then clamp the
     result to the viewport. This also covers wide, multi-column flyouts. */
  const dropdownViewportGap = 16;
  let dropdownPositionFrame = null;

  function overflowScore(rect, viewportWidth) {
    return Math.max(0, dropdownViewportGap - rect.left)
      + Math.max(0, rect.right - (viewportWidth - dropdownViewportGap));
  }

  function positionDropdown(menu) {
    menu.classList.remove('header__dropdown-menu--align-end', 'header__dropdown-menu--open-start');
    menu.style.removeProperty('--dropdown-viewport-shift');
    menu.style.removeProperty('--dropdown-vertical-shift');
    menu.style.removeProperty('max-width');
    menu.style.removeProperty('max-height');

    const parentMenu = menu.parentElement?.closest('.header__dropdown-menu');
    const isNested = Boolean(parentMenu);
    const anchorRect = isNested ? menu.parentElement.getBoundingClientRect() : null;
    const alternativeClass = isNested
      ? 'header__dropdown-menu--open-start'
      : 'header__dropdown-menu--align-end';
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const maxColumns = Number(menu.dataset.dropdownMaxColumns || 1);
    const itemCount = Number(menu.dataset.dropdownItemCount || menu.children.length);

    if (isNested && maxColumns > 1) {
      const isRtl = getComputedStyle(menu).direction === 'rtl';

      /* Measure one column using the exact same width as a regular dropdown,
         then derive how many whole columns fit on either side. */
      menu.style.setProperty('--dropdown-column-count', '1');
      menu.style.setProperty('--dropdown-row-count', `${itemCount}`);
      const columnWidth = menu.getBoundingClientRect().width;
      const availableLeft = Math.max(0, anchorRect.left - dropdownViewportGap);
      const availableRight = Math.max(0, viewportWidth - dropdownViewportGap - anchorRect.right);
      const columnsLeft = Math.floor(availableLeft / columnWidth);
      const columnsRight = Math.floor(availableRight / columnWidth);
      const actualColumns = Math.min(maxColumns, Math.max(1, columnsLeft, columnsRight));
      const opensToLeft = columnsLeft > columnsRight || (columnsLeft === columnsRight && isRtl);

      menu.classList.toggle('header__dropdown-menu--open-start', opensToLeft !== isRtl);
      menu.style.setProperty('--dropdown-column-count', `${actualColumns}`);
      menu.style.setProperty('--dropdown-row-count', `${Math.ceil(itemCount / actualColumns)}`);
    } else {
      const defaultScore = overflowScore(menu.getBoundingClientRect(), viewportWidth);

      if (defaultScore > 0) {
        menu.classList.add(alternativeClass);
        const alternativeScore = overflowScore(menu.getBoundingClientRect(), viewportWidth);

        if (defaultScore <= alternativeScore) {
          menu.classList.remove(alternativeClass);
        }
      }
    }

    let rect = menu.getBoundingClientRect();

    /* A nested flyout must fit beside its parent dropdown. If neither side
       has its full intrinsic width available, shrink the panel on the side
       with the most room instead of translating it across the parent links. */
    if (isNested) {
      const opensToRight = rect.left >= anchorRect.right - 1;
      const availableWidth = opensToRight
        ? viewportWidth - dropdownViewportGap - anchorRect.right
        : anchorRect.left - dropdownViewportGap;

      if (rect.width > availableWidth) {
        menu.style.maxWidth = `${Math.max(1, Math.floor(availableWidth))}px`;
        rect = menu.getBoundingClientRect();
      }
      const viewportTop = Math.max(dropdownViewportGap, menu.closest('header')?.getBoundingClientRect().bottom || 0);
      const viewportBottom = viewportHeight - dropdownViewportGap;
      const availableHeight = Math.max(1, viewportBottom - viewportTop);

      if (rect.height > availableHeight) {
        menu.style.maxHeight = `${Math.floor(availableHeight)}px`;
        rect = menu.getBoundingClientRect();
      }

      const itemIndex = Array.from(parentMenu.children).indexOf(menu.parentElement.closest('li')) + 1;
      const preferredTop = itemCount > itemIndex
        ? parentMenu.getBoundingClientRect().top
        : anchorRect.bottom - rect.height;
      const top = Math.max(viewportTop, Math.min(preferredTop, viewportBottom - rect.height));
      const verticalShift = top - rect.top;

      if (verticalShift) {
        menu.style.setProperty('--dropdown-vertical-shift', `${verticalShift}px`);
      }
    }

    let shift = 0;

    if (rect.left < dropdownViewportGap) {
      shift = dropdownViewportGap - rect.left;
    } else if (rect.right > viewportWidth - dropdownViewportGap) {
      shift = viewportWidth - dropdownViewportGap - rect.right;
    }

    if (shift) {
      menu.style.setProperty('--dropdown-viewport-shift', `${shift}px`);
    }
  }

  function positionOpenDropdowns() {
    dropdownPositionFrame = null;

    document.querySelectorAll('.shopify-section--header .header__menu-disclosure[open]').forEach((details) => {
      if (document.documentElement.clientWidth < 1000 || details.closest('header')?.getBoundingClientRect().bottom <= 0) {
        if (details.getAttribute('aria-expanded') !== 'false') details.parentElement.close();
        return;
      }

      const menu = details.lastElementChild;

      if (menu?.classList.contains('header__dropdown-menu')) {
        positionDropdown(menu);
      }
    });
  }

  function queueDropdownPositioning() {
    if (dropdownPositionFrame !== null) return;
    dropdownPositionFrame = requestAnimationFrame(positionOpenDropdowns);
  }

  document.addEventListener('toggle', ({ target }) => {
    if (target.open && target.matches('.shopify-section--header .header__menu-disclosure')) {
      queueDropdownPositioning();
    }
  }, true);
  window.addEventListener('resize', queueDropdownPositioning, { passive: true });
  window.addEventListener('scroll', queueDropdownPositioning, { passive: true });

  /* Footer — open Google Maps links in new tab */
  document.querySelectorAll('.shopify-section--footer a[href*="maps.google"]').forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener');
  });

  /* Collection bubbles — free scrolling with a synchronized progress indicator. */
  class CollectionBubblesScrollbar extends HTMLElement {
    connectedCallback() {
      this.carousel = document.getElementById(this.getAttribute('aria-controls'));
      this.range = this.querySelector('input[type="range"]');

      if (!this.carousel || !this.range) {
        this.hidden = true;
        return;
      }

      this.abortController = new AbortController();
      const listenerOptions = { signal: this.abortController.signal };

      this.carousel.addEventListener('scroll', this.queueProgressUpdate, { ...listenerOptions, passive: true });
      this.range.addEventListener('input', this.scrollFromRange, listenerOptions);

      this.resizeObserver = new ResizeObserver(this.updateLayout);
      this.resizeObserver.observe(this.carousel);
      this.updateLayout();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();

      if (this.updateFrame) {
        cancelAnimationFrame(this.updateFrame);
        this.updateFrame = null;
      }
    }

    queueProgressUpdate = () => {
      if (this.updateFrame) return;

      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.updateProgress();
      });
    };

    scrollFromRange = () => {
      const scrollLeft = this.maxScroll * (this.range.value / 100) * (document.dir === 'rtl' ? -1 : 1);

      this.carousel.scrollTo({ left: scrollLeft, behavior: 'auto' });
    };

    updateProgress() {
      const progress = this.maxScroll > 0 ? Math.abs(this.carousel.scrollLeft) / this.maxScroll : 0;

      this.range.value = `${Math.min(1, Math.max(0, progress)) * 100}`;
    }

    updateLayout = () => {
      const { clientWidth, scrollWidth } = this.carousel;
      this.maxScroll = scrollWidth - clientWidth;
      this.hidden = this.maxScroll <= 1;

      if (this.hidden) return;

      const carouselStyles = getComputedStyle(this.carousel);
      const gap = parseFloat(carouselStyles.columnGap) || 0;
      const startPadding = parseFloat(carouselStyles.scrollPaddingInlineStart) || 0;
      const cellWidth = this.carousel.firstElementChild?.offsetWidth || 1;
      const visibleCells = Math.max(1, Math.min(
        this.carousel.children.length,
        Math.floor((clientWidth - startPadding + gap) / (cellWidth + gap)) - 1,
      ));
      const thumbWidth = Math.max(32, Math.round(this.range.clientWidth * clientWidth / scrollWidth));

      if (this.carousel.getAttribute('group-cells') !== `${visibleCells}`) {
        this.carousel.setAttribute('group-cells', `${visibleCells}`);
      }
      this.range.style.setProperty('--collection-bubbles-thumb-width', `${thumbWidth}px`);
      this.updateProgress();
    };
  }

  if (!customElements.get('collection-bubbles-scrollbar')) {
    customElements.define('collection-bubbles-scrollbar', CollectionBubblesScrollbar);
  }

  /* Collection infinite scroll — Shopify still server-renders every page.
     JavaScript progressively appends section-rendered chunks and stores a
     product anchor so reload/back can rebuild the list without losing place. */
  class CollectionInfiniteScroll extends HTMLElement {
    static stateVersion = 1;

    connectedCallback() {
      if (this.initialized) return;

      this.productList = this.querySelector(':scope > product-list');
      this.pagination = this.querySelector('[data-infinite-pagination]');
      this.controls = this.querySelector('[data-infinite-controls]');
      this.progress = this.querySelector('[data-infinite-progress]');
      this.loadMoreButton = this.querySelector('[data-infinite-load-more]');
      this.status = this.querySelector('[data-infinite-status]');
      this.skeletonTemplate = this.querySelector('template[data-infinite-skeleton-card]');

      if (!this.productList || !this.controls || !this.loadMoreButton || !this.skeletonTemplate) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.currentPage = Number(this.dataset.currentPage || 1);
      this.totalPages = Number(this.dataset.totalPages || 1);
      this.totalProducts = Number(this.dataset.totalProducts || 0);
      this.productsPerPage = Number(this.dataset.productsPerPage || 48);
      this.nextUrl = this.dataset.nextUrl || '';
      this.loadedPages = new Set([this.currentPage]);
      this.boundImages = new WeakSet();
      this.contextKey = this.buildContextKey();
      this.storageKey = `collection-infinite-scroll:${this.contextKey}`;
      const savedState = this.getRestorableState();
      this.restoring = Boolean(savedState);

      this.productList.querySelectorAll(':scope > product-card, :scope > .product-list__promo').forEach((item) => {
        item.dataset.collectionPage ||= `${this.currentPage}`;
      });
      this.itemsByPage = new Map([
        [this.currentPage, [...this.productList.querySelectorAll(`:scope > [data-collection-page="${this.currentPage}"]`)]],
      ]);

      this.classList.add('is-enhanced');
      this.controls.hidden = false;

      const listenerOptions = { signal: this.abortController.signal };
      this.addEventListener('click', this.onClick, listenerOptions);
      window.addEventListener('scroll', this.queueScrollUpdate, { ...listenerOptions, passive: true });
      window.addEventListener('resize', this.queueStateSave, { ...listenerOptions, passive: true });
      window.addEventListener('pagehide', this.onPageHide, listenerOptions);
      window.addEventListener('pageshow', this.onPageShow, listenerOptions);

      this.loadObserver = new IntersectionObserver(this.onLoadIntersection, {
        rootMargin: '1000px 0px',
      });
      this.imageObserver = new IntersectionObserver(this.onImageIntersection, {
        rootMargin: '600px 0px',
      });

      this.prepareProductImages(this.productList);
      this.updateControls();

      if (savedState) {
        this.restore(savedState);
      } else {
        this.observeLoadMore();
        this.queueStateSave();
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.loadObserver?.disconnect();
      this.imageObserver?.disconnect();

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.saveTimer) clearTimeout(this.saveTimer);
    }

    buildContextKey() {
      const url = new URL(window.location.href);
      const parameters = [...url.searchParams.entries()]
        .filter(([name]) => name !== 'page' && name !== 'section_id')
        .sort(([nameA, valueA], [nameB, valueB]) => (
          nameA.localeCompare(nameB) || valueA.localeCompare(valueB)
        ));

      url.search = '';
      parameters.forEach(([name, value]) => url.searchParams.append(name, value));

      return `${url.pathname}${url.search}`;
    }

    getRestorableState() {
      if (this.isValidState(this.preloadedRestoreState)) {
        const state = this.preloadedRestoreState;
        this.preloadedRestoreState = null;
        return state;
      }

      const historyState = history.state?.collectionInfiniteScroll;
      if (this.isValidState(historyState)) return historyState;

      const navigationType = performance.getEntriesByType('navigation')[0]?.type;
      if (!['reload', 'back_forward'].includes(navigationType)) return null;

      try {
        const storedState = JSON.parse(sessionStorage.getItem(this.storageKey));
        return this.isValidState(storedState) ? storedState : null;
      } catch {
        return null;
      }
    }

    isValidState(state) {
      return Boolean(
        state
        && state.version === CollectionInfiniteScroll.stateVersion
        && state.contextKey === this.contextKey
        && Array.isArray(state.loadedPages)
      );
    }

    onClick = (event) => {
      const retryButton = event.target.closest('[data-infinite-retry-page]');
      if (retryButton) {
        const page = Number(retryButton.dataset.infiniteRetryPage);
        const placeholder = this.productList.querySelector(`[data-page-placeholder="${page}"]`);
        if (placeholder) this.loadRestoredPage(page, placeholder);
        return;
      }

      if (event.target.closest('[data-infinite-load-more]')) {
        this.loadNextPage();
        return;
      }

      const productLink = event.target.closest('product-card a[href*="/products/"]');
      if (!productLink || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      this.navigationAnchor = productLink.closest('product-card');
      const anchorPage = Number(this.navigationAnchor?.dataset.collectionPage || this.currentPage);
      this.currentPage = anchorPage;
      this.persistState({ anchor: this.navigationAnchor, updateUrl: true });
    };

    onPageHide = () => {
      this.persistState({ anchor: this.navigationAnchor, updateUrl: false });
    };

    onPageShow = async (event) => {
      const returnState = history.state?.collectionInfiniteScroll;
      this.navigationAnchor = null;

      if (event.persisted) {
        this.observeLoadMore();
        await this.waitForLayout();
        if (this.isValidState(returnState)) this.restoreAnchor(returnState);
        this.updateVisiblePage();
        this.persistState({ updateUrl: true });
      }
    };

    onLoadIntersection = (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
    };

    onImageIntersection = (entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle(
          'is-image-loading--active',
          entry.isIntersecting && entry.target.classList.contains('is-image-loading'),
        );
      });
    };

    prepareProductImages(root) {
      root.querySelectorAll('.product-card__image--primary').forEach((image) => {
        const media = image.closest('.product-card__media');
        if (!media || this.boundImages.has(image)) return;

        this.boundImages.add(image);

        const markLoaded = () => {
          media.classList.remove('is-image-loading', 'is-image-loading--active');
          this.imageObserver.unobserve(media);
        };

        if (image.complete && image.naturalWidth > 0) {
          markLoaded();
          return;
        }

        media.classList.add('is-image-loading');
        image.addEventListener('load', markLoaded, { once: true, signal: this.abortController.signal });
        image.addEventListener('error', markLoaded, { once: true, signal: this.abortController.signal });
        this.imageObserver.observe(media);
      });
    }

    observeLoadMore() {
      this.loadObserver?.disconnect();
      if (this.nextUrl && !this.loading && !this.restoring && !this.classList.contains('has-error') && !this.loadMoreButton.hidden) {
        this.loadObserver.observe(this.loadMoreButton);
      }
    }

    queueScrollUpdate = () => {
      if (this.scrollFrame || this.restoring) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateVisiblePage();
        this.queueStateSave();
      });
    };

    queueStateSave = () => {
      if (this.restoring) return;

      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.persistState({ updateUrl: true }), 120);
    };

    waitForLayout() {
      return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    }

    updateVisiblePage() {
      const pages = this.getLoadedPageNumbers();
      if (!pages.length) return;

      const viewportTarget = window.innerHeight * 0.42;
      let closestPage = pages[0];
      let closestDistance = Number.POSITIVE_INFINITY;

      pages.forEach((page) => {
        const bounds = this.getPageBounds(page);
        if (!bounds) return;

        const distance = viewportTarget < bounds.top
          ? bounds.top - viewportTarget
          : viewportTarget > bounds.bottom
            ? viewportTarget - bounds.bottom
            : 0;

        if (distance < closestDistance) {
          closestPage = page;
          closestDistance = distance;
        }
      });

      this.currentPage = closestPage;
    }

    getLoadedPageNumbers() {
      return [...this.loadedPages].sort((a, b) => a - b);
    }

    getPageElements(page) {
      return this.itemsByPage.get(Number(page)) || [];
    }

    getPageBounds(page) {
      const elements = this.getPageElements(page);
      if (!elements.length) return null;

      const firstRect = elements[0].getBoundingClientRect();
      const columnCount = getComputedStyle(this.productList).gridTemplateColumns.split(' ').length;
      const bottom = Math.max(
        ...elements.slice(-columnCount).map((element) => element.getBoundingClientRect().bottom),
      );
      return {
        top: firstRect.top,
        bottom,
      };
    }

    getPageHeight(page) {
      const bounds = this.getPageBounds(page);
      if (!bounds) return null;

      const rowGap = parseFloat(getComputedStyle(this.productList).rowGap) || 0;
      return Math.max(1, Math.round(bounds.bottom - bounds.top + rowGap));
    }

    findAnchorCard() {
      const cards = [...this.productList.querySelectorAll(':scope > product-card')];
      if (!cards.length) return null;

      const target = Math.min(160, window.innerHeight * 0.2);
      let closestCard = null;
      let closestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;

        const distance = Math.abs(rect.top - target);
        if (distance < closestDistance) {
          closestCard = card;
          closestDistance = distance;
        }
      });

      return closestCard;
    }

    buildState(anchor = null) {
      const anchorCard = anchor || this.findAnchorCard();
      const anchorRect = anchorCard?.getBoundingClientRect();
      const pageHeights = {};

      this.getLoadedPageNumbers().forEach((page) => {
        const height = this.getPageHeight(page);
        if (height) pageHeights[page] = height;
      });

      return {
        version: CollectionInfiniteScroll.stateVersion,
        contextKey: this.contextKey,
        currentPage: Number(anchorCard?.dataset.collectionPage || this.currentPage || 1),
        loadedPages: this.getLoadedPageNumbers(),
        pageHeights,
        anchorHandle: anchorCard?.getAttribute('handle') || null,
        anchorOffset: anchorRect?.top ?? null,
        scrollY: window.scrollY,
      };
    }

    persistState({ anchor = null, updateUrl = false } = {}) {
      if (!this.isConnected) return;

      const state = this.buildState(anchor);
      this.currentPage = state.currentPage;
      const targetUrl = updateUrl ? this.createPageUrl(state.currentPage) : window.location.href;

      try {
        history.replaceState({
          ...(history.state || {}),
          collectionInfiniteScroll: state,
        }, '', targetUrl);
      } catch {
        // The list still works when history state is unavailable.
      }

      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(state));
      } catch {
        // Storage can be blocked by the browser; pagination remains the fallback.
      }
    }

    createPageUrl(page) {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');

      if (page > 1) {
        url.searchParams.set('page', page);
      } else {
        url.searchParams.delete('page');
      }

      return url.toString();
    }

    estimatePageHeight() {
      const measuredHeights = this.getLoadedPageNumbers()
        .map((page) => this.getPageHeight(page))
        .filter(Boolean);

      return measuredHeights.at(-1) || Math.max(window.innerHeight, this.productList.scrollHeight || 0);
    }

    createPlaceholder(page, height = null) {
      const placeholder = document.createElement('div');
      placeholder.className = 'collection-infinite-scroll__page-placeholder';
      placeholder.dataset.pagePlaceholder = `${page}`;
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.style.setProperty('--collection-placeholder-height', `${height || this.estimatePageHeight()}px`);
      this.renderPlaceholderSkeleton(placeholder, page);
      return placeholder;
    }

    renderPlaceholderSkeleton(placeholder, page = Number(placeholder.dataset.pagePlaceholder)) {
      placeholder.replaceChildren();
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.classList.remove('is-error');

      const grid = document.createElement('div');
      grid.className = 'collection-infinite-scroll__skeleton-grid';

      const productsBeforePage = Math.max(0, page - 1) * this.productsPerPage;
      const skeletonCount = Math.min(
        this.productsPerPage,
        Math.max(1, this.totalProducts - productsBeforePage),
      );

      for (let index = 0; index < skeletonCount; index += 1) {
        grid.append(this.skeletonTemplate.content.cloneNode(true));
      }

      placeholder.append(grid);
    }

    showPlaceholderError(placeholder, page) {
      placeholder.replaceChildren();
      placeholder.removeAttribute('aria-hidden');
      placeholder.classList.add('is-error');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button button--outline';
      button.dataset.infiniteRetryPage = `${page}`;
      button.textContent = this.dataset.loadErrorLabel;
      placeholder.append(button);
    }

    async fetchPage(pageUrl, page) {
      const url = new URL(pageUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.abortController.signal,
      });

      if (!response.ok) throw new Error(`Collection page ${page} returned ${response.status}`);

      const parsedDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const sourceController = parsedDocument.querySelector('collection-infinite-scroll');
      const sourceList = sourceController?.querySelector(':scope > product-list');

      if (!sourceController || !sourceList) throw new Error(`Collection page ${page} has no product list`);

      const sourceItems = [...sourceList.children].filter((item) => (
        item.matches('product-card') || (page === 1 && item.matches('.product-list__promo'))
      ));

      if (!sourceItems.length) throw new Error(`Collection page ${page} has no products`);

      const items = sourceItems.map((item) => {
        const importedItem = document.importNode(item, true);
        importedItem.dataset.collectionPage = `${page}`;
        importedItem.removeAttribute('reveal-on-scroll');
        return importedItem;
      });

      return {
        items,
        nextUrl: sourceController.dataset.nextUrl || '',
        paginationHtml: sourceController.querySelector('[data-infinite-pagination]')?.innerHTML || '',
      };
    }

    replacePlaceholder(placeholder, items) {
      const anchorCard = this.findCardByHandle(this.restoringAnchorHandle) || this.findAnchorCard();
      const anchorTopBefore = anchorCard?.getBoundingClientRect().top;
      const fragment = document.createDocumentFragment();
      items.forEach((item) => fragment.append(item));
      placeholder.replaceWith(fragment);
      items.forEach((item) => this.prepareProductImages(item));

      if (anchorCard && anchorTopBefore != null) {
        const anchorTopAfter = anchorCard.getBoundingClientRect().top;
        window.scrollBy(0, anchorTopAfter - anchorTopBefore);
      }
    }

    updateAfterPageLoad(page, result) {
      this.loadedPages.add(page);
      this.itemsByPage.set(page, result.items);

      if (page === Math.max(...this.loadedPages)) {
        this.nextUrl = result.nextUrl;
        this.dataset.nextUrl = result.nextUrl;
        if (this.pagination && result.paginationHtml) this.pagination.innerHTML = result.paginationHtml;
      }

      this.updateControls();
      if (!this.restoring) this.queueStateSave();
    }

    async loadPage(page, placeholder, pageUrl = this.createPageUrl(page)) {
      const result = await this.fetchPage(pageUrl, page);
      this.replacePlaceholder(placeholder, result.items);
      this.updateAfterPageLoad(page, result);
      return result;
    }

    async loadNextPage() {
      if (this.loading || this.restoring || !this.nextUrl) return;

      this.loading = true;
      this.classList.remove('has-error');
      this.loadObserver.disconnect();
      this.loadMoreButton.disabled = true;
      this.loadMoreButton.textContent = this.dataset.loadingLabel;

      const pageUrl = new URL(this.nextUrl, window.location.origin);
      const page = Number(pageUrl.searchParams.get('page') || Math.max(...this.loadedPages) + 1);
      const placeholder = this.createPlaceholder(page);
      this.productList.append(placeholder);

      try {
        const result = await this.loadPage(page, placeholder, pageUrl.toString());
        if (this.status) {
          const loadedCount = result.items.filter((item) => item.matches('product-card')).length;
          this.status.textContent = this.dataset.loadedLabel.replace('[count]', `${loadedCount}`);
        }
      } catch {
        placeholder.remove();
        this.classList.add('has-error');
        this.loadMoreButton.textContent = this.dataset.loadErrorLabel;
      } finally {
        this.loading = false;
        this.loadMoreButton.disabled = false;
        this.updateControls();
        this.observeLoadMore();
      }
    }

    async loadRestoredPage(page, placeholder) {
      if (placeholder.dataset.loading === 'true') return;

      placeholder.dataset.loading = 'true';
      if (placeholder.classList.contains('is-error')) this.renderPlaceholderSkeleton(placeholder, page);

      try {
        await this.loadPage(page, placeholder);
      } catch {
        this.showPlaceholderError(placeholder, page);
      } finally {
        delete placeholder.dataset.loading;
      }
    }

    findCardByHandle(handle) {
      if (!handle) return null;
      return this.productList.querySelector(`:scope > product-card[handle="${CSS.escape(handle)}"]`);
    }

    restoreAnchor(state) {
      const anchorCard = this.findCardByHandle(state.anchorHandle);

      if (anchorCard && state.anchorOffset != null) {
        const targetScroll = window.scrollY + anchorCard.getBoundingClientRect().top - state.anchorOffset;
        window.scrollTo({ top: Math.max(0, targetScroll), behavior: 'auto' });
      } else if (Number.isFinite(state.scrollY)) {
        window.scrollTo({ top: Math.max(0, state.scrollY), behavior: 'auto' });
      }
    }

    async restore(state) {
      this.restoring = true;
      this.restoringAnchorHandle = state.anchorHandle;
      const pagesToRestore = [...new Set(state.loadedPages.map(Number))]
        .filter((page) => page >= 1 && page <= this.totalPages && page !== this.currentPage);
      const previousPages = pagesToRestore.filter((page) => page < this.currentPage).sort((a, b) => a - b);
      const followingPages = pagesToRestore.filter((page) => page > this.currentPage).sort((a, b) => a - b);
      const placeholders = new Map();
      const previousFragment = document.createDocumentFragment();

      previousPages.forEach((page) => {
        const placeholder = this.productList.querySelector(`[data-page-placeholder="${page}"]`)
          || this.createPlaceholder(page, state.pageHeights?.[page]);
        placeholders.set(page, placeholder);

        if (!placeholder.isConnected) previousFragment.append(placeholder);
      });

      if (previousFragment.childNodes.length) this.productList.prepend(previousFragment);

      followingPages.forEach((page) => {
        const placeholder = this.createPlaceholder(page, state.pageHeights?.[page]);
        placeholders.set(page, placeholder);
        this.productList.append(placeholder);
      });

      await this.waitForLayout();
      this.restoreAnchor(state);

      const queue = [...pagesToRestore].sort((pageA, pageB) => (
        Math.abs(pageA - this.currentPage) - Math.abs(pageB - this.currentPage)
      ));
      const worker = async () => {
        while (queue.length) {
          const page = queue.shift();
          await this.loadRestoredPage(page, placeholders.get(page));
        }
      };

      await Promise.all([worker(), worker()]);
      await document.fonts?.ready;
      await this.waitForLayout();
      this.restoreAnchor(state);
      this.restoringAnchorHandle = null;
      this.restoring = false;
      this.updateControls();
      this.observeLoadMore();
      this.persistState({ updateUrl: true });
    }

    updateControls() {
      const loadedProductCount = this.productList.querySelectorAll(':scope > product-card').length;

      if (this.progress) {
        this.progress.textContent = this.dataset.progressLabel
          .replace('[count]', `${loadedProductCount}`)
          .replace('[total]', `${this.totalProducts}`);
      }

      this.loadMoreButton.hidden = !this.nextUrl;
      this.loadMoreButton.disabled = this.loading || this.restoring;
      if (!this.loading && !this.classList.contains('has-error')) {
        this.loadMoreButton.textContent = this.dataset.loadMoreLabel;
      }
    }
  }

  if (!customElements.get('collection-infinite-scroll')) {
    customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
  }

  class CartRelatedProducts extends HTMLElement {
    connectedCallback() {
      if (!this.hidden) return;
      this.abortController = new AbortController();
      const drawer = this.closest('cart-drawer');
      drawer?.addEventListener('dialog:before-show', this.load, { signal: this.abortController.signal });
      if (drawer?.hasAttribute('open')) this.load();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.loading = false;
    }

    async fetchRecommendations(productId, signal) {
      const parameters = new URLSearchParams({
        product_id: productId,
        section_id: this.dataset.sectionId,
        limit: '10',
        intent: 'related',
      });
      const response = await fetch(`${Shopify.routes.root}recommendations/products?${parameters}`, { signal });
      if (!response.ok) throw new Error(`Recommendations: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      return [...html.querySelectorAll('template[data-cart-recommendation]')];
    }

    load = async () => {
      if (this.loading || this.loaded) return;
      this.loading = true;
      const { signal } = this.abortController;
      const productIds = [...new Set(this.dataset.productIds.split(',').filter(Boolean))];
      const seen = new Set(productIds);
      const cards = [];
      let failed = false;

      for (let offset = 0; offset < productIds.length && cards.length < 4; offset += 3) {
        const results = await Promise.allSettled(
          productIds.slice(offset, offset + 3).map((productId) => this.fetchRecommendations(productId, signal)),
        );

        if (signal.aborted || !this.isConnected) return;

        failed ||= results.some((result) => result.status === 'rejected');
        const queues = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);

        while (cards.length < 4 && queues.some((queue) => queue.length)) {
          for (const queue of queues) {
            const recommendation = queue.shift();
            if (!recommendation || seen.has(recommendation.dataset.cartRecommendation)) continue;
            const card = recommendation.content.firstElementChild;
            if (!card) continue;
            seen.add(recommendation.dataset.cartRecommendation);
            cards.push(document.importNode(card, true));
            if (cards.length === 4) break;
          }
        }
      }

      this.querySelector('[data-cart-related-list]').replaceChildren(...cards);
      this.hidden = cards.length === 0;
      this.loaded = cards.length > 0 || !failed;
      this.loading = false;
    };
  }

  if (!customElements.get('cart-related-products')) {
    customElements.define('cart-related-products', CartRelatedProducts);
  }

  /* Upsell — fetch complementary products and render */
  function formatMoney(cents) {
    if (Shopify.formatMoney) return Shopify.formatMoney(cents);
    return `${(cents / 100).toFixed(2).replace('.', ',')} DKK`;
  }

  function escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function buildProductCard(product, { addLabel, soldOutLabel, saveLabel }) {
    const variant = product.variants[0];
    const price = formatMoney(variant.price);
    const imageUrl = product.featured_image
      ? product.featured_image.replace(/(\.[a-zA-Z]+)(\?|$)/, '_160x$1$2')
      : '';

    let saveBadge = '';
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const pct = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
      saveBadge = `<p class="product-upsell__save">${saveLabel ? saveLabel.replace('[pct]', pct) : `Spar ${pct}%`}</p>`;
    }

    const image = imageUrl
      ? `<a href="${product.url}" class="product-upsell__image">
           <img src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy" width="80">
         </a>`
      : '';

    const button = variant.available
      ? `<button type="button" class="product-upsell__add" data-upsell-variant-id="${variant.id}">${addLabel} – ${price}</button>`
      : `<button type="button" class="product-upsell__add product-upsell__add--sold-out" disabled>${soldOutLabel}</button>`;

    return `
      <div class="product-upsell__item">
        <div class="product-upsell__item-top">
          ${image}
          <div class="product-upsell__info">
            <a href="${product.url}" class="product-upsell__title">${escapeHtml(product.title)}</a>
            ${saveBadge}
          </div>
        </div>
        ${button}
      </div>`;
  }

  document.querySelectorAll('[data-upsell]').forEach(async (el) => {
    const { productId, limit = 3, heading, addLabel, soldOutLabel, saveLabel } = el.dataset;
    const url = `${Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=complementary`;

    try {
      const { products } = await (await fetch(url)).json();
      if (!products?.length) { el.remove(); return; }

      const headingHtml = heading ? `<p class="product-upsell__heading">${heading}</p>` : '';
      const cards = products.map((p) => buildProductCard(p, { addLabel, soldOutLabel, saveLabel })).join('');

      el.innerHTML = `<div class="product-upsell">${headingHtml}<div class="product-upsell__list">${cards}</div></div>`;
      el.hidden = false;
    } catch {
      el.remove();
    }
  });

  /* Upsell — delegated add-to-cart */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.product-upsell__add');
    if (!btn || btn.disabled) return;

    const variantId = btn.dataset.upsellVariantId;
    if (!variantId) return;

    btn.disabled = true;
    const originalText = btn.textContent;

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      btn.textContent = '✓';
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
      setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 1500);
    } catch {
      btn.disabled = false;
    }
  });
})();

/* Cart drawer — terms checkbox gates checkout button */
(function () {
  const drawer = document.querySelector('#cart-drawer');
  if (!drawer) return;

  let boundCheckbox = null;

  function initTermsGate() {
    const checkbox = drawer.querySelector('#cart-drawer-terms');
    const checkoutBtn = drawer.querySelector('.button-group .button[name="checkout"]');
    if (!checkbox || !checkoutBtn) return;
    if (checkbox === boundCheckbox) return;

    boundCheckbox = checkbox;

    function toggle() {
      checkoutBtn.disabled = !checkbox.checked;
    }

    toggle();
    checkbox.addEventListener('change', toggle);
  }

  initTermsGate();

  const observer = new MutationObserver(initTermsGate);
  observer.observe(drawer, { childList: true, subtree: true });
})();
